// Build a deep-link URL that takes a recipient straight into a specific
// puzzle. The URL bakes in the seed + difficulty + generator version so a
// future generator change cleanly invalidates older links rather than
// silently rendering the wrong puzzle. Optional `from` / `time` drive the
// "Alice solved this in 4:23 — beat her time?" banner.

import type { Difficulty } from '../game/types.js';
import { GENERATOR_VERSION } from '../generator/Carver.js';

const SEED_HEX_WIDTH = 8;
const SEED_HEX_RADIX = 16;
const U32_MAX = 0xffffffff;

export interface EncodeShareUrlInput {
  readonly seed: number;
  readonly difficulty: Difficulty;
  readonly from?: string;
  readonly timeMs?: number;
}

/**
 * Build the absolute share URL for a puzzle.
 *
 * Uses `window.location.origin` + `import.meta.env.BASE_URL` so dev (e.g.
 * `http://localhost:5176/sudoku/`) and prod (`https://bn-game.netlify.app/
 * sudoku/`) produce different but correct URLs without conditional code.
 *
 * Throws if `seed` is not a u32 — callers should clamp upstream.
 */
export function encodeShareUrl(input: EncodeShareUrlInput): string {
  const { seed, difficulty, from, timeMs } = input;
  assertU32(seed);

  const params = new URLSearchParams();
  params.set('seed', seed.toString(SEED_HEX_RADIX).padStart(SEED_HEX_WIDTH, '0'));
  params.set('d', difficulty);
  params.set('v', String(GENERATOR_VERSION));

  const safeFrom = from === undefined ? null : sanitizeFrom(from);
  if (safeFrom !== null && safeFrom.length > 0) {
    params.set('from', safeFrom);
  }
  if (timeMs !== undefined && isNonNegativeFiniteInt(timeMs)) {
    params.set('time', String(Math.floor(timeMs)));
  }

  return `${window.location.origin}${import.meta.env.BASE_URL}?${params.toString()}`;
}

const FROM_MAX_LEN = 24;
// Allow letters, digits, spaces, and a small punctuation set. Strips control
// chars / HTML-special chars defensively — the value is rendered to the
// recipient and we don't trust the sender to behave.
const FROM_DISALLOWED = /[^\p{L}\p{N} _\-.'!?,]/gu;

/**
 * Trim and strip a sender name so it's safe to round-trip through the URL and
 * render in a DOM text node. Exposed because `decode.ts` runs the same pass
 * on the inbound side — defensive symmetry.
 */
export function sanitizeFrom(value: string): string {
  return value.replace(FROM_DISALLOWED, '').trim().slice(0, FROM_MAX_LEN);
}

function assertU32(seed: number): void {
  if (!Number.isInteger(seed) || seed < 0 || seed > U32_MAX) {
    throw new RangeError(`encodeShareUrl: seed must be a u32, got ${seed}`);
  }
}

function isNonNegativeFiniteInt(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}
